/**
 * GestorDashboard — 3 abas: Saúde, Clima, Engajamento
 * Redesenhado conforme Dashboards_briefing_geral.pdf v1.0
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  RefreshCw, Download, Filter, TrendingUp, TrendingDown,
  Minus, AlertTriangle, AlertCircle, ShieldAlert,
  Users, BookOpen, BarChart3, Heart, Activity,
  ArrowUp, ArrowDown,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Paleta de cores (briefing seção 9) ────────────────────────────────────────
const COLORS = {
  brand:    '#534AB7',
  positive: { bg: '#EAF3DE', text: '#3B6D11' },
  atencao:  { bg: '#FFF8E1', text: '#BA7517' },
  risco:    { bg: '#FDF0E8', text: '#C8541A' },
  critico:  { bg: '#FEE8E8', text: '#A32D2D' },
  t0:       '#B4B2A9',
  IQR:      '#534AB7',
  IEO:      '#0F6E56',
  IPR:      '#C8541A',
};

const INSTRUMENT_NAMES = {
  IQR: 'Qualidade Relacional',
  IEO: 'Empatia Organizacional',
  IPR: 'Pertencimento Relacional',
};

// Escala máxima por instrumento (IPR é 1-4, outros 1-5)
const MAX_SCALE = { IQR: 5, IEO: 5, IPR: 4 };

// ── Alert badge ───────────────────────────────────────────────────────────────
const AlertBadge = ({ alert }) => {
  const colors = COLORS[alert.level?.toLowerCase()] || COLORS.atencao;
  const icons = { CRITICO: ShieldAlert, RISCO: AlertCircle, ATENCAO: AlertTriangle };
  const Icon = icons[alert.level] || AlertTriangle;
  return (
    <div
      className="flex items-start gap-3 px-4 py-3 rounded-xl border"
      style={{ background: colors.bg, borderColor: colors.text + '33', color: colors.text }}
    >
      <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div>
        <p className="font-semibold text-sm">{alert.o_que}</p>
        {alert.o_que_fazer && (
          <p className="text-xs mt-0.5 opacity-80">{alert.o_que_fazer}</p>
        )}
      </div>
    </div>
  );
};

// ── Grouped bar chart (T0 + T1 side by side, vertical) ───────────────────────
const BAR_HEIGHT = 200; // px máximo

const BarChart = ({ data }) => {
  // data = { IQR: {T0: {total}, T1: {total}}, IEO: ..., IPR: ... }
  const instruments = ['IQR', 'IEO', 'IPR'];

  return (
    <div className="flex items-end justify-around gap-6">
      {instruments.map(inst => {
        const instData = data[inst] || {};
        const t0 = instData.T0;
        const t1 = instData.T1 || instData.T2;
        const maxScale = MAX_SCALE[inst];
        const hasData = t0 || t1;

        const barPx = (score) =>
          score !== undefined && score !== null
            ? Math.round((score / maxScale) * BAR_HEIGHT)
            : 0;

        const t0Score = t0?.dimensions?.total ?? t0?.total ?? null;
        const t1Score = t1?.dimensions?.total ?? t1?.total ?? null;
        const delta = t0Score !== null && t1Score !== null
          ? (t1Score - t0Score).toFixed(2)
          : null;

        return (
          <div key={inst} className="flex flex-col items-center gap-1 flex-1">
            {/* Barras */}
            <div
              className="flex items-end gap-2 w-full justify-center"
              style={{ height: BAR_HEIGHT + 'px' }}
            >
              {/* T0 */}
              {t0Score !== null ? (
                <div className="flex flex-col items-center gap-1">
                  <span className="text-xs font-semibold text-gray-600">
                    {t0Score.toFixed(1)}
                  </span>
                  <div
                    className="w-8 rounded-t-md transition-all"
                    style={{ height: barPx(t0Score) + 'px', background: COLORS.t0 }}
                    title={`T0: ${t0Score.toFixed(2)}`}
                  />
                </div>
              ) : (
                <div className="w-8 flex items-center justify-center h-6 text-xs text-gray-300">—</div>
              )}
              {/* T1 */}
              {t1Score !== null ? (
                <div className="flex flex-col items-center gap-1">
                  <span className="text-xs font-semibold" style={{ color: COLORS[inst] }}>
                    {t1Score.toFixed(1)}
                  </span>
                  <div
                    className="w-8 rounded-t-md transition-all"
                    style={{ height: barPx(t1Score) + 'px', background: COLORS[inst] }}
                    title={`T1: ${t1Score.toFixed(2)}`}
                  />
                </div>
              ) : (
                <div className="w-8 flex items-center justify-center h-6 text-xs text-gray-300">—</div>
              )}
            </div>

            {/* Delta */}
            <div className="text-xs font-medium flex items-center gap-1 h-5">
              {delta !== null ? (
                <>
                  {parseFloat(delta) > 0 ? (
                    <ArrowUp className="w-3 h-3 text-green-600" />
                  ) : parseFloat(delta) < 0 ? (
                    <ArrowDown className="w-3 h-3 text-red-600" />
                  ) : (
                    <Minus className="w-3 h-3 text-gray-400" />
                  )}
                  <span className={parseFloat(delta) > 0 ? 'text-green-600' : parseFloat(delta) < 0 ? 'text-red-600' : 'text-gray-400'}>
                    {parseFloat(delta) > 0 ? '+' : ''}{delta}
                  </span>
                </>
              ) : (
                <span className="text-gray-300">—</span>
              )}
            </div>

            {/* Nome */}
            <p className="text-xs font-semibold text-center" style={{ color: COLORS[inst] }}>
              {inst}
            </p>
            <p className="text-xs text-gray-500 text-center leading-tight">
              {INSTRUMENT_NAMES[inst]}
            </p>
            {!hasData && (
              <p className="text-xs text-gray-300 text-center">Sem dados</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Legenda T0 / T1
const BarLegend = () => (
  <div className="flex gap-4 justify-center text-xs text-gray-500 mt-3">
    <span className="flex items-center gap-1">
      <span className="inline-block w-3 h-3 rounded" style={{ background: COLORS.t0 }} />
      T0 (baseline)
    </span>
    <span className="flex items-center gap-1">
      <span className="inline-block w-3 h-3 rounded" style={{ background: COLORS.brand }} />
      T1 (atual)
    </span>
  </div>
);

// ── Crossing card (cruzamento diagnóstico) ────────────────────────────────────
const CrossingCard = ({ crossing }) => {
  const severityColors = {
    POSITIVO: COLORS.positive,
    ATENCAO:  COLORS.atencao,
    RISCO:    COLORS.risco,
    CRITICO:  COLORS.critico,
  };
  const c = severityColors[crossing.severity] || COLORS.atencao;
  return (
    <div
      className="p-4 rounded-xl border"
      style={{ background: c.bg, borderColor: c.text + '33' }}
    >
      <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: c.text }}>
        {crossing.label}
      </p>
      <p className="text-sm font-medium" style={{ color: c.text }}>
        {crossing.text}
      </p>
    </div>
  );
};

// ── Mini stat card ────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon: Icon, color }) => (
  <Card className="p-4 text-center">
    {Icon && <Icon className="w-5 h-5 mx-auto mb-1" style={{ color: color || COLORS.brand }} />}
    <p className="text-2xl font-bold" style={{ color: color || COLORS.brand }}>{value ?? '—'}</p>
    <p className="text-xs text-gray-600 mt-0.5">{label}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </Card>
);

// ── PDF Report generator ──────────────────────────────────────────────────────

const DIM_LABELS = {
  voz: 'Voz', travessia: 'Travessia', receptividade: 'Receptividade',
  tomada_perspectiva: 'Tomada de Perspectiva', cuidado_compassivo: 'Cuidado Compassivo',
  presenca_empatica: 'Presença Empática', pertencimento_vinculo: 'Pertencimento e Vínculo',
  confianca_relacional: 'Confiança Relacional', solidao: 'Solidão',
  seguranca_discordar: 'Segurança para Discordar',
  total: 'Score Total',
};

function generateHRReportPDF({ group, scores, alerts, crossings, groupData, npsData, department }) {
  const today = new Date().toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' });
  const INST_COLORS = { IQR: '#534AB7', IEO: '#0F6E56', IPR: '#C8541A' };
  const INST_NAMES = { IQR: 'Qualidade Relacional', IEO: 'Empatia Organizacional', IPR: 'Pertencimento Relacional' };
  const MAX_SCALE = { IQR: 5, IEO: 5, IPR: 4 };

  const severityColors = {
    POSITIVO: { bg: '#EAF3DE', border: '#3B6D11', text: '#3B6D11', label: '✅ Positivo' },
    ATENCAO:  { bg: '#FFF8E1', border: '#BA7517', text: '#BA7517', label: '⚠️ Atenção'  },
    RISCO:    { bg: '#FDF0E8', border: '#C8541A', text: '#C8541A', label: '🔶 Risco'    },
    CRITICO:  { bg: '#FEE8E8', border: '#A32D2D', text: '#A32D2D', label: '🚨 Crítico'  },
  };

  function instrSection(inst) {
    const instData = scores[inst];
    if (!instData) return `<p style="color:#9ca3af;font-size:13px;margin:8px 0;">Sem dados para ${inst}.</p>`;
    const color = INST_COLORS[inst];
    const maxScale = MAX_SCALE[inst];

    let html = '';
    ['T0', 'T1', 'T2'].forEach(moment => {
      const md = instData[moment];
      if (!md) return;
      if (md.insufficient_data) {
        html += `<p style="font-size:12px;color:#9ca3af;margin:4px 0;">${moment}: aguardando mínimo de respondentes (${md.respondents || 0} de ${md.min_required || 5})</p>`;
        return;
      }
      const dims = md.dimensions || {};
      const total = dims.total;
      const publicDims = Object.entries(dims).filter(([k]) => !k.startsWith('_') && k !== 'solidao_raw' && k !== 'secao_b_coletivo' && k !== 'total');

      html += `
        <div style="margin-top:10px;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
            <span style="background:${color};color:#fff;font-size:11px;font-weight:700;padding:2px 8px;border-radius:4px;">${moment}</span>
            ${total !== undefined ? `<span style="font-size:13px;font-weight:700;color:${color};">Score: ${total.toFixed(2)} / ${maxScale}</span>` : ''}
            <span style="font-size:11px;color:#9ca3af;">(${md.respondents} respondente${md.respondents !== 1 ? 's' : ''})</span>
          </div>
          ${publicDims.map(([dim, score]) => {
            const pct = Math.round((score / maxScale) * 100);
            const isLow = (maxScale === 5 && score < 2.5) || (maxScale === 4 && score < 2.0);
            return `
              <div style="margin-bottom:5px;">
                <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:2px;">
                  <span style="color:#4b5563;">${DIM_LABELS[dim] || dim.replace(/_/g, ' ')}</span>
                  <span style="color:${isLow ? '#C8541A' : '#374151'};font-weight:${isLow ? '700' : '400'};">${score.toFixed(2)} / ${maxScale}</span>
                </div>
                <div style="background:#e5e7eb;border-radius:4px;height:6px;">
                  <div style="background:${isLow ? '#C8541A' : color};width:${pct}%;height:6px;border-radius:4px;"></div>
                </div>
              </div>`;
          }).join('')}
        </div>`;
    });
    return html || `<p style="color:#9ca3af;font-size:13px;">Sem janelas aplicadas.</p>`;
  }

  const alertsHtml = alerts.length === 0
    ? '<p style="color:#6b7280;font-size:13px;">Sem alertas no momento.</p>'
    : alerts.map(a => {
        const level = a.level || 'ATENCAO';
        const c = { CRITICO: '#FEE8E8', RISCO: '#FDF0E8', ATENCAO: '#FFF8E1' };
        const tc = { CRITICO: '#A32D2D', RISCO: '#C8541A', ATENCAO: '#BA7517' };
        return `
          <div style="background:${c[level] || '#FFF8E1'};border-left:4px solid ${tc[level] || '#BA7517'};border-radius:6px;padding:10px 14px;margin-bottom:8px;">
            <p style="font-weight:700;font-size:13px;color:${tc[level] || '#BA7517'};">[${level}] ${a.o_que || ''}</p>
            ${a.o_que_fazer ? `<p style="font-size:12px;color:#4b5563;margin-top:3px;">${a.o_que_fazer}</p>` : ''}
          </div>`;
      }).join('');

  const crossingsHtml = crossings.length === 0
    ? '<p style="color:#6b7280;font-size:13px;">Sem cruzamentos disponíveis no momento.</p>'
    : crossings.map(cx => {
        const sev = cx.severity || 'ATENCAO';
        const sc = severityColors[sev] || severityColors.ATENCAO;
        return `
          <div style="background:${sc.bg};border:1px solid ${sc.border}33;border-radius:8px;padding:12px 14px;margin-bottom:8px;">
            <p style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:${sc.text};margin-bottom:4px;">${sc.label} · ${cx.label || ''}</p>
            <p style="font-size:13px;color:${sc.text};">${cx.text || ''}</p>
          </div>`;
      }).join('');

  const npsScore = npsData?.overall?.nps_score;

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <title>Relatório RH — ${group || 'Grupo'}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', system-ui, sans-serif; color: #1f2937; background: #fff; padding: 36px; line-height: 1.5; }
    .header { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #534AB7; padding-bottom: 18px; margin-bottom: 28px; }
    .logo { font-size: 26px; font-weight: 800; color: #534AB7; letter-spacing: -1px; }
    .logo span { color: #a78bfa; }
    .header-right { text-align: right; font-size: 12px; color: #6b7280; }
    .section { margin-bottom: 28px; page-break-inside: avoid; }
    .section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #6b7280; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; margin-bottom: 14px; }
    .overview-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
    .stat-card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 14px; text-align: center; }
    .stat-value { font-size: 24px; font-weight: 800; color: #534AB7; }
    .stat-label { font-size: 11px; color: #6b7280; margin-top: 2px; }
    .inst-card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 16px; margin-bottom: 12px; page-break-inside: avoid; }
    .inst-header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
    .inst-badge { color: #fff; font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 5px; }
    .footer { margin-top: 36px; padding-top: 16px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 11px; color: #9ca3af; }
    @media print { body { padding: 20px; } .section { break-inside: avoid; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">sim<span>by</span></div>
    <div class="header-right">
      <div style="font-weight:700;color:#111827;">Relatório de RH — Cruzamento de Indicadores</div>
      <div>${group || 'Grupo'}${department ? ' · ' + department : ''}</div>
      <div>Emitido em: ${today}</div>
    </div>
  </div>

  <!-- Visão geral -->
  <div class="section">
    <div class="section-title">📊 Visão Geral do Grupo</div>
    <div class="overview-grid">
      <div class="stat-card">
        <div class="stat-value">${groupData?.total_users ?? '—'}</div>
        <div class="stat-label">Colaboradores</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="color:#0F6E56;">${groupData?.active_users_7d ?? '—'}</div>
        <div class="stat-label">Ativos (7 dias)</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="color:${npsScore >= 50 ? '#1A5C3A' : npsScore >= 0 ? '#BA7517' : '#A32D2D'};">${npsScore !== null && npsScore !== undefined ? (npsScore >= 0 ? '+' : '') + npsScore : '—'}</div>
        <div class="stat-label">NPS do Treinamento</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${groupData?.total_lessons_completed ?? '—'}</div>
        <div class="stat-label">Lições concluídas</div>
      </div>
    </div>
  </div>

  <!-- Instrumentos -->
  <div class="section">
    <div class="section-title">📈 Instrumentos de Medição (IQR · IEO · IPR)</div>
    ${['IQR', 'IEO', 'IPR'].map(inst => `
      <div class="inst-card">
        <div class="inst-header">
          <span class="inst-badge" style="background:${INST_COLORS[inst]};">${inst}</span>
          <span style="font-size:14px;font-weight:600;color:#111827;">${INST_NAMES[inst]}</span>
          <span style="font-size:12px;color:#9ca3af;">Escala 1–${MAX_SCALE[inst]}</span>
        </div>
        ${instrSection(inst)}
      </div>
    `).join('')}
  </div>

  <!-- Cruzamentos -->
  <div class="section">
    <div class="section-title">🔗 Cruzamentos Diagnósticos</div>
    ${crossingsHtml}
  </div>

  <!-- Alertas -->
  <div class="section">
    <div class="section-title">🚨 Alertas Ativos</div>
    ${alertsHtml}
  </div>

  <div class="footer">
    <p>Relatório gerado pela plataforma Simby · Treinamento em Inteligência Relacional</p>
    <p style="margin-top:3px;">Este documento é confidencial. Uso exclusivo da gestão de RH.</p>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=900,height=1000');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.onload = () => { setTimeout(() => win.print(), 300); };
}

// ══════════════════════════════════════════════════════════════════════════════
const GestorDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [activeTab, setActiveTab] = useState('saude');
  const [isLoading, setIsLoading] = useState(true);
  const [department, setDepartment] = useState('');
  const [departments, setDepartments] = useState([]);

  // Instruments data (new)
  const [instrumentData, setInstrumentData] = useState(null);

  // Legacy engagement data
  const [groupData, setGroupData] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [npsData, setNpsData] = useState(null);

  const fetchAll = async (signal) => {
    setIsLoading(true);
    const deptParam = department ? `?department=${department}` : '';
    const cfg = { withCredentials: true, signal };
    try {
      const [instrRes, deptRes, groupRes, detailRes, npsRes] = await Promise.allSettled([
        axios.get(`${API}/instruments/group-scores${deptParam}`, cfg),
        axios.get(`${API}/hr/gestor/departments`, cfg),
        axios.get(`${API}/hr/gestor/my-group${deptParam}`, cfg),
        axios.get(`${API}/hr/gestor/my-group/detail${deptParam}`, cfg),
        axios.get(`${API}/nps/gestor/my-group/nps${deptParam}`, cfg),
      ]);

      if (instrRes.status === 'fulfilled') setInstrumentData(instrRes.value.data);
      if (deptRes.status === 'fulfilled')  setDepartments(deptRes.value.data.departments || []);
      if (groupRes.status === 'fulfilled') setGroupData(groupRes.value.data);
      if (detailRes.status === 'fulfilled') setGroupDetail(detailRes.value.data);
      if (npsRes.status === 'fulfilled')   setNpsData(npsRes.value.data);
    } catch (e) {
      if (e?.name !== 'CanceledError' && e?.name !== 'AbortError') {
        console.error('GestorDashboard fetchAll error:', e);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchAll(controller.signal);
    return () => controller.abort();
  }, [department]);

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/hr/gestor/my-group/export`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `simby-grupo-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
    } catch { alert('Erro ao exportar dados.'); }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-purple-600 mx-auto mb-4" />
          <p className="text-gray-500">Carregando painel…</p>
        </div>
      </div>
    );
  }

  const alerts = instrumentData?.alerts || [];
  const crossings = instrumentData?.crossings || [];
  const scores = instrumentData?.scores || {};
  const npsScore = npsData?.overall?.nps_score;

  // ── Tabs ────────────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'saude',      label: 'Saúde Relacional' },
    { id: 'clima',      label: 'Clima' },
    { id: 'engajamento', label: 'Engajamento' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Alertas fixos no topo — prioridade visual máxima */}
      {alerts.length > 0 && (
        <div className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-200 px-4 py-2">
          <div className="max-w-5xl mx-auto space-y-1">
            {alerts.map((a, i) => <AlertBadge key={i} alert={a} />)}
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Painel do Gestor</h1>
            <p className="text-sm text-gray-500">{user?.group || 'Seu grupo'}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Filtro de departamento */}
            {departments.length > 0 && (
              <select
                value={department}
                onChange={e => setDepartment(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              >
                <option value="">Todos os departamentos</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            )}
            <Button variant="outline" size="sm" onClick={fetchAll}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Atualizar
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="w-4 h-4 mr-1" />
              Exportar CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => generateHRReportPDF({
                group: user?.group,
                scores,
                alerts,
                crossings,
                groupData,
                npsData,
                department,
              })}
              className="text-purple-700 border-purple-300 hover:bg-purple-50"
            >
              <Download className="w-4 h-4 mr-1" />
              Relatório PDF
            </Button>
          </div>
        </div>

        {/* Overview cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Colaboradores" value={groupData?.total_users} icon={Users} />
          <StatCard label="Ativos (7d)" value={groupData?.active_users_7d} icon={Activity} color="#0F6E56" />
          <StatCard label="NPS do Treinamento"
            value={npsScore !== null && npsScore !== undefined ? (npsScore >= 0 ? '+' : '') + npsScore : '—'}
            icon={BarChart3}
            color={npsScore >= 50 ? '#1A5C3A' : npsScore >= 0 ? '#BA7517' : '#A32D2D'}
          />
          <StatCard label="Lições concluídas" value={groupDetail?.total_lessons_completed} icon={BookOpen} />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-purple-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── ABA: SAÚDE ─────────────────────────────────────────────────── */}
        {activeTab === 'saude' && (
          <div className="space-y-6">
            {/* Gráfico de barras T0+T1 */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Evolução dos Índices</h2>
                  <p className="text-xs text-gray-500">Comparativo T0 (baseline) → T1</p>
                </div>
              </div>
              {Object.keys(scores).length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Sem dados de instrumentos disponíveis.</p>
                  <p className="text-xs mt-1">As janelas IQR, IEO e IPR precisam ser aplicadas pelo Admin.</p>
                </div>
              ) : (
                <>
                  <BarChart data={scores} />
                  <BarLegend />
                  {instrumentData?.min_respondents_met === false && (
                    <p className="text-xs text-center text-amber-600 mt-3">
                      Aguardando mais respostas para exibir os dados do time
                      (mínimo {instrumentData?.min_respondents ?? 5} respondentes por janela).
                    </p>
                  )}
                </>
              )}
            </Card>

            {/* Dimensões por instrumento */}
            {['IQR', 'IEO', 'IPR'].map(inst => {
              const instData = scores[inst];
              if (!instData) return null;
              const latestMoment = ['T1', 'T2', 'T0'].find(m => instData[m] && !instData[m].insufficient_data);
              if (!latestMoment) return null;
              const dims = instData[latestMoment]?.dimensions || {};
              const publicDims = Object.entries(dims).filter(
                ([k]) => !k.startsWith('_') && k !== 'solidao_raw' && k !== 'secao_b_coletivo' && k !== 'total'
              );
              if (!publicDims.length) return null;
              const maxScale = MAX_SCALE[inst];

              return (
                <Card key={inst} className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className="px-2 py-0.5 rounded text-xs font-bold text-white"
                      style={{ background: COLORS[inst] }}
                    >
                      {inst}
                    </span>
                    <h3 className="font-semibold text-gray-800 text-sm">{INSTRUMENT_NAMES[inst]}</h3>
                    <span className="text-xs text-gray-400">({latestMoment})</span>
                  </div>
                  <div className="space-y-2">
                    {publicDims.map(([dim, score]) => {
                      const pct = Math.round((score / maxScale) * 100);
                      const isLow = (maxScale === 5 && score < 2.5) || (maxScale === 4 && score < 2.0);
                      return (
                        <div key={dim}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-600 capitalize">{dim.replace(/_/g, ' ')}</span>
                            <span className={isLow ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                              {score.toFixed(2)} / {maxScale}
                            </span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div
                              className="h-2 rounded-full"
                              style={{
                                width: pct + '%',
                                background: isLow ? '#C8541A' : COLORS[inst],
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* ── ABA: CLIMA ─────────────────────────────────────────────────── */}
        {activeTab === 'clima' && (
          <div className="space-y-4">
            {crossings.length === 0 ? (
              <Card className="p-8 text-center text-gray-400">
                <p className="text-sm">Sem dados suficientes para gerar leituras diagnósticas.</p>
                <p className="text-xs mt-1">É necessário pelo menos T0 de dois instrumentos para calcular os cruzamentos.</p>
              </Card>
            ) : (
              <>
                <h2 className="text-lg font-bold text-gray-900">Leitura Diagnóstica</h2>
                <p className="text-sm text-gray-500">
                  Cruzamentos calculados automaticamente com base nos scores mais recentes do time.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {crossings.map((c, i) => <CrossingCard key={i} crossing={c} />)}
                </div>
              </>
            )}

            {/* Alertas detalhados */}
            {alerts.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-800 mb-3">Ativações Sugeridas</h3>
                <div className="space-y-2">
                  {alerts.map((a, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 p-4 rounded-xl border"
                      style={{
                        background: (COLORS[a.level?.toLowerCase()] || COLORS.atencao).bg,
                        borderColor: (COLORS[a.level?.toLowerCase()] || COLORS.atencao).text + '33',
                        color: (COLORS[a.level?.toLowerCase()] || COLORS.atencao).text,
                      }}
                    >
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm">{a.o_que}</p>
                        <p className="text-xs mt-0.5">{a.o_que_fazer}</p>
                        {a.dimension && (
                          <span className="text-xs opacity-60 mt-1 block">
                            Dimensão: {a.dimension.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── ABA: ENGAJAMENTO ───────────────────────────────────────────── */}
        {activeTab === 'engajamento' && (
          <div className="space-y-5">
            <h2 className="text-lg font-bold text-gray-900">Métricas de Uso</h2>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard label="Total de lições" value={groupDetail?.total_lessons_completed} icon={BookOpen} />
              <StatCard label="Quizzes feitos" value={groupDetail?.total_quizzes_completed} icon={BarChart3} color="#534AB7" />
              <StatCard label="Simuladores" value={groupDetail?.total_simulators_completed} icon={Activity} color="#0F6E56" />
            </div>

            {/* Distribuição por nível */}
            {groupDetail?.level_distribution && (
              <Card className="p-5">
                <h3 className="font-semibold text-gray-800 mb-3 text-sm">Distribuição por Nível</h3>
                <div className="space-y-2">
                  {Object.entries(groupDetail.level_distribution).sort(([a], [b]) => Number(a) - Number(b)).map(([lvl, cnt]) => {
                    const total = Object.values(groupDetail.level_distribution).reduce((a, b) => a + b, 0);
                    const pct = total > 0 ? Math.round((cnt / total) * 100) : 0;
                    return (
                      <div key={lvl}>
                        <div className="flex justify-between text-xs mb-1 text-gray-600">
                          <span>Nível {lvl}</span>
                          <span>{cnt} pessoas ({pct}%)</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className="h-2 rounded-full"
                            style={{ width: pct + '%', background: COLORS.brand }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* NPS do treinamento */}
            {npsData?.overall && (
              <Card className="p-5">
                <h3 className="font-semibold text-gray-800 mb-3 text-sm">NPS do Treinamento</h3>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: 'Score NPS', value: (npsScore >= 0 ? '+' : '') + npsScore, color: npsScore >= 50 ? '#1A5C3A' : npsScore >= 0 ? '#BA7517' : '#A32D2D' },
                    { label: 'Promotores', value: npsData.overall.promoters || 0, color: '#1A5C3A' },
                    { label: 'Neutros', value: npsData.overall.neutrals || 0, color: '#BA7517' },
                    { label: 'Detratores', value: npsData.overall.detractors || 0, color: '#A32D2D' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="text-center">
                      <p className="text-xl font-bold" style={{ color }}>{value}</p>
                      <p className="text-xs text-gray-500">{label}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  {npsData.overall.total || 0} respostas totais
                </p>
              </Card>
            )}

            {/* Ilhas mais acessadas */}
            {groupDetail?.top_islands?.length > 0 && (
              <Card className="p-5">
                <h3 className="font-semibold text-gray-800 mb-3 text-sm">Ilhas mais acessadas</h3>
                <div className="space-y-2">
                  {groupDetail.top_islands.slice(0, 5).map((island, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{island.name || island.island_id}</span>
                      <span className="text-gray-500">{island.completions} conclusões</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GestorDashboard;
